/**
 * Attachment validation helpers for the /help/contact page.
 *
 * WHAT THIS FILE DOES:
 * - Validates attachment files against allowed MIME types and extensions.
 * - Enforces size limits before upload.
 * - Provides human-readable attachment type labels.
 *
 * WHY THIS EXISTS:
 * - Keeps attachment logic small and testable.
 * - Avoids repeating MIME/type checks in the form controller.
 * - Mirrors backend validation rules for fast client feedback.
 *
 * ARCHITECTURE CONTEXT:
 * - Used by support-form-controller.ts to validate FileList inputs.
 * - Backend enforces the same constraints in support-messages handlers.
 *
 * RELATED FILES:
 * - packages/backend/src/api/routes/support-messages/attachment-handler.ts
 * - packages/backend/src/api/routes/support-messages/constants.ts
 * - packages/chrome-plugin/extension/ui/options/support-message-modal.js
 *
 * NON-GOALS:
 * - No upload logic (handled by support-message-api.ts).
 * - No UI state management (handled by support-form-controller.ts).
 *
 * FILE HEADER SIZE TARGET:
 * - Keep this header between 50-120 lines for LLM readability.
 */

import {
  AttachmentErrors,
  AttachmentBlockedExtensions,
  AttachmentDirectExtensionsSet,
  AttachmentDirectMimeTypesSet,
  AttachmentTypeLabels,
  AttachmentTranscodeVideoExtensionsSet,
  AttachmentTranscodeVideoMimeTypesSet,
  Limits
} from './constants.js';

/**
 * Extract a lowercase file extension from a filename.
 *
 * @param fileName - Filename including extension.
 * @returns Extension without dot, or null.
 */
function getFileExtension(fileName: string): string | null {
  const parts = fileName.toLowerCase().split('.');
  if (parts.length < 2) {
    return null;
  }
  return parts.pop() ?? null;
}

/**
 * Normalize a MIME type by stripping parameters.
 *
 * @param mimeType - MIME type string.
 * @returns Normalized MIME type.
 */
function normalizeMimeType(mimeType: string): string {
  const trimmed = mimeType.trim();
  if (!trimmed) return '';
  return trimmed.split(';')[0]?.trim() ?? '';
}

/**
 * Decide whether a file is an allowed attachment.
 *
 * @param file - Incoming attachment.
 * @returns Tuple of allowed flag + error message.
 */
export function validateAttachment(file: File): { allowed: boolean; error?: string } {
  if (!file || file.size === 0) {
    return { allowed: false, error: AttachmentErrors.EMPTY };
  }
  if (file.size > Limits.MAX_ATTACHMENT_BYTES) {
    return { allowed: false, error: AttachmentErrors.TOO_LARGE };
  }

  const extension = getFileExtension(file.name ?? '');
  if (extension && AttachmentBlockedExtensions.has(extension)) {
    return { allowed: false, error: AttachmentErrors.EXECUTABLE };
  }

  const normalizedMimeType = normalizeMimeType(file.type);
  if (normalizedMimeType && AttachmentDirectMimeTypesSet.has(normalizedMimeType)) {
    return { allowed: true };
  }

  if (normalizedMimeType && AttachmentTranscodeVideoMimeTypesSet.has(normalizedMimeType)) {
    return { allowed: true };
  }

  const canUseExtensionFallback = normalizedMimeType === '' || normalizedMimeType === 'application/octet-stream';
  if (canUseExtensionFallback && extension && (AttachmentDirectExtensionsSet.has(extension) || AttachmentTranscodeVideoExtensionsSet.has(extension))) {
    return { allowed: true };
  }

  return { allowed: false, error: AttachmentErrors.TYPE_UNSUPPORTED };
}

/**
 * Build a label describing the attachment type.
 *
 * @param file - Attachment file.
 * @returns Label string.
 */
export function resolveAttachmentTypeLabel(file: File): string {
  const normalizedMimeType = normalizeMimeType(file.type);
  if (normalizedMimeType === 'application/pdf') {
    return AttachmentTypeLabels.PDF;
  }
  if (AttachmentDirectMimeTypesSet.has(normalizedMimeType) && normalizedMimeType.startsWith('image/')) {
    return AttachmentTypeLabels.IMAGE;
  }
  if (AttachmentTranscodeVideoMimeTypesSet.has(normalizedMimeType) || normalizedMimeType.startsWith('video/')) {
    return AttachmentTypeLabels.VIDEO;
  }

  const extension = getFileExtension(file.name ?? '');
  if (extension && AttachmentDirectExtensionsSet.has(extension)) {
    if (extension === 'pdf') {
      return AttachmentTypeLabels.PDF;
    }
    return AttachmentTypeLabels.IMAGE;
  }
  if (extension && AttachmentTranscodeVideoExtensionsSet.has(extension)) {
    return AttachmentTypeLabels.VIDEO;
  }

  return AttachmentTypeLabels.FILE;
}

/**
 * Format bytes to a human-readable string.
 *
 * @param bytes - File size in bytes.
 * @returns Human-friendly size string.
 */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${bytes} B`;
}
